import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { AI_COMPONENT_TYPE_KEYS } from '@terra/ui-ai-applications';
import {
  AiComponentTypeSchema,
  type AiComponentDto,
  type AiComponentListGroupDto,
} from '@terra/shared-contracts-ai-applications';
import { useListPageGroupCollapse } from '../../common/useListPageGroupCollapse';
import {
  AI_COMPONENTS_EMPTY_GROUP_KEY,
  buildAiComponentsTableRows,
  isAiComponentsGroupHeaderRow,
  resolveAiComponentsLoadMoreRowIndex,
  type AiComponentsGroupByField,
} from './ai-components-table.model';

export function useAiComponentsTableRows({
  components,
  groupBy,
  groups,
  resetKey,
}: {
  components: readonly AiComponentDto[];
  groupBy: AiComponentsGroupByField | undefined;
  groups: readonly AiComponentListGroupDto[];
  resetKey: string;
}) {
  const tAssets = useTranslations('aiApplications.assets');
  const tComponentTypes = useTranslations('aiApplications.componentTypes');
  const tTable = useTranslations('proxy.assets.table');
  const {
    collapsedGroupKeys,
    toggleGroup,
    expandAllGroups,
    collapseGroups,
    areAllCollapsed,
  } = useListPageGroupCollapse({ resetKey });

  const getGroupLabel = useCallback(
    (groupKey: string): string => {
      if (groupKey === AI_COMPONENTS_EMPTY_GROUP_KEY) {
        return tTable('emptyGroup');
      }

      switch (groupBy) {
        case 'type': {
          const type = AiComponentTypeSchema.safeParse(groupKey);

          return type.success
            ? tComponentTypes(AI_COMPONENT_TYPE_KEYS[type.data])
            : groupKey;
        }

        case 'isInternal':
          return groupKey === 'true'
            ? tAssets('internal')
            : tAssets('external');
        case 'role':
        case undefined:
          return groupKey;
      }
    },
    [groupBy, tAssets, tComponentTypes, tTable],
  );

  const rows = useMemo(
    () =>
      buildAiComponentsTableRows({
        components,
        groupBy,
        groups,
        collapsedGroupKeys,
        getGroupLabel,
      }),
    [collapsedGroupKeys, components, getGroupLabel, groupBy, groups],
  );
  const loadMoreRowIndex = resolveAiComponentsLoadMoreRowIndex({
    rows,
    groupBy,
    collapsedGroupKeys,
  });
  const groupKeys = useMemo(
    () =>
      rows.flatMap((row) =>
        isAiComponentsGroupHeaderRow(row) ? [row.groupKey] : [],
      ),
    [rows],
  );

  return {
    rows,
    loadMoreRowIndex,
    collapsedGroupKeys,
    areAllGroupsCollapsed: areAllCollapsed(groupKeys),
    toggleGroup,
    expandAllGroups,
    collapseAllGroups: () => collapseGroups(groupKeys),
  };
}
